import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Footer, Header, Page, PageContent } from "@/components/page.tsx";
import { Trans, useTranslation } from "react-i18next";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal.tsx";
import { useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import {
  getPaymentFromSessionStorage,
  PaymentType,
} from "@/features/payment/utils";
import { CollectorsDiscountData } from "@/hooks/use-apply-payment";
import {
  CollectorsDiscountModal,
  useAutocloseModalAfterMs,
} from "@/components/collectors-discount-modal";
import { useAudioSlot } from "@/features/audio/kiosk-audio";
import { NTH_PURCHASE_CAMPAIGN_QUERY_KEY } from "@frontend-common/hooks/use-nth-purchase-campaign";

export const Route = createFileRoute("/bubbles-loves-you-screen")({
  component: RouteComponent,
  loader: ({ context }) => {
    // the purchase used up any nth-purchase-campaign hold; don't wait for the next poll to drop the banner
    void context.queryClient.invalidateQueries({
      queryKey: NTH_PURCHASE_CAMPAIGN_QUERY_KEY,
    });
    const payment = getPaymentFromSessionStorage();
    if (payment) {
      const mutation = context.queryClient.getMutationCache().find({
        mutationKey: ["apply-payment"],
        predicate: (mutation) => {
          return (
            (mutation.state.variables as { payment: PaymentType })?.payment
              ?.kioskPaymentNonceKey === payment.kioskPaymentNonceKey
          );
        },
      });

      return {
        collectosDiscountData: mutation?.state.data as
          | CollectorsDiscountData
          | undefined,
      };
    }
    return {};
  },
});

function RouteComponent() {
  useAudioSlot("page.bubbles-loves-you");
  useTranslation();
  const { showModal } = useMuiModal();
  const { collectosDiscountData } = Route.useLoaderData();

  useEffect(() => {
    async function showModals() {
      if (collectosDiscountData) {
        await showModal(
          <CollectorsDiscountModal data={collectosDiscountData} />,
        );
      }
      const isNewRegistration = !!sessionStorage.getItem("is_new_registration");

      if (isNewRegistration) {
        await showModal(<ActivateRegistrationModal />);
      }
      showModal(<AskForNewTransactionModal />);
    }

    showModals();
  }, []);

  return (
    <Page className={`bg-background`}>
      <Header showUserDetails={false}>
        <img
          src={`/bubbles-logo-on-dark.svg`}
          className={`h-16 w-auto mx-auto mb-16`}
        />
      </Header>
      <PageContent>
        <h2 className={`font-helvetica font-bold text-6xl text-center mb-16`}>
          <Trans>Köszönjük!</Trans>
        </h2>
        <p className={`text-3xl text-center`}>
          <Trans>Bubbles, a Te mosodád</Trans>
        </p>
        <img src={`/bubbles-loves-you.svg`} />
      </PageContent>
      <Footer
        helpText={
          <Trans>
            Köszönjük, hogy a Bubbles-t választottad! A kioszk hamarosan
            visszaáll a kezdő képernyőre.
          </Trans>
        }
      />
    </Page>
  );
}

const AskForNewTransactionModal = () => {
  useAudioSlot("modal.ask-new-transaction");
  const navigate = useNavigate();
  const { closeModal } = useMuiModal();

  const wasClosedByThisComponent = useRef(false);

  const onClose = () => {
    wasClosedByThisComponent.current = true;
    navigate({ to: `/start-screen`, reloadDocument: true });
    closeModal();
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      onClose();
    }, 10000);

    return () => {
      clearTimeout(timeout);
      sessionStorage.removeItem("coupon");
      if (!wasClosedByThisComponent.current) {
        onClose();
      }
    };
  }, []);

  return (
    <Modal>
      <ModalTitle className={`text-[56px] leading-16`}>
        <Trans>Szeretnél azonnal egy másik gépet is elindítani?</Trans>
      </ModalTitle>
      <ModalContent>
        <div className="flex gap-6">
          <Button
            onClick={() => {
              onClose();
            }}
          >
            <Trans>Nem</Trans>
          </Button>
          <Button
            onClick={() => {
              closeModal();
              wasClosedByThisComponent.current = true;
              navigate({ to: "/machine-type-selector-screen" });
            }}
          >
            <Trans>Igen</Trans>
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};

export const ActivateRegistrationModal = () => {
  useAudioSlot("modal.activate-registration");
  const { closeModal } = useMuiModal();
  useAutocloseModalAfterMs(10_000);

  return (
    <Modal>
      <ModalTitle>
        <Trans>Bubbles fiók aktiválása</Trans>
      </ModalTitle>
      <ModalContent>
        <Trans>
          Köszönjük, hogy regisztráltál! A második belépés előtt aktiválnod kell
          a Bubbles fiókod. Az aktiváláshoz kattints az "Aktiváld Bubbles
          fiókod" tárgyú emailben kiküldött linkre.
        </Trans>
        <div className="flex justify-center items-center">
          <Button
            onClick={() => {
              closeModal();
            }}
          >
            <Trans>Rendben</Trans>
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
};
